import { useRef } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { MoveRight } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { AvatarInfo, Button } from '@chillout/ui'
import { AdminModal, ModalActions } from '../shared/Modal'

interface RewardAdjustConfirmModalProps {
  visible: boolean
  memberName: string
  memberEmail: string
  rewardName: string
  currentCount: number
  rewardDelta: number
  reason: string
  note: string
  onConfirm: () => void
  onCancel: () => void
}

export function RewardAdjustConfirmModal({
  visible,
  memberName,
  memberEmail,
  rewardName,
  currentCount,
  rewardDelta,
  reason,
  note,
  onConfirm,
  onCancel,
}: RewardAdjustConfirmModalProps) {
  const { t } = useTranslation()

  const snapshot = useRef({ memberName, memberEmail, rewardName, currentCount, rewardDelta, reason, note })
  if (visible) {
    snapshot.current = { memberName, memberEmail, rewardName, currentCount, rewardDelta, reason, note }
  }
  const s = snapshot.current
  const isRemoving = s.rewardDelta < 0
  const abs = Math.abs(s.rewardDelta)
  const afterCount = Math.max(0, s.currentCount + s.rewardDelta)

  const title = isRemoving
    ? t('members.detail.rewardAdjustment.confirmTitle_remove', { count: abs, name: s.rewardName })
    : t('members.detail.rewardAdjustment.confirmTitle_add', { count: abs, name: s.rewardName })

  const confirmLabel = isRemoving
    ? t('members.detail.rewardAdjustment.confirmButton_remove', { count: abs }) as string
    : t('members.detail.rewardAdjustment.confirmButton_add', { count: abs }) as string

  const body = isRemoving
    ? t('members.detail.rewardAdjustment.confirmBody_remove', { count: abs, name: s.rewardName })
    : t('members.detail.rewardAdjustment.confirmBody_add', { count: abs, name: s.rewardName })

  const reasonLabel = t(`common.adjustmentReasons.${s.reason}`, { defaultValue: s.reason })

  return (
    <AdminModal
      visible={visible}
      onClose={onCancel}
      closeAccessibilityLabel={t('common.cancel')}
      title={title}
    >
      <View className="mt-4 gap-4">
        <AvatarInfo name={s.memberName} email={s.memberEmail} />

        <Text className="text-sm font-normal text-secondary">{body}</Text>

        <View className="flex-row items-center justify-center rounded-lg bg-canvas-primary px-4 py-3">
          <View className="flex-row items-baseline gap-1">
            <Text className="text-base font-bold text-primary">{s.rewardName}</Text>
            <Text className="text-base font-bold text-primary">×{s.currentCount}</Text>
          </View>
          <View className="px-3">
            <MoveRight size={16} color={colors.text.secondary} strokeWidth={2} />
          </View>
          <View className="flex-row items-baseline gap-1">
            <Text className="text-base font-bold text-primary">{s.rewardName}</Text>
            <Text className="text-base font-bold text-accent">×{afterCount}</Text>
          </View>
        </View>

        <View className="gap-2">
          <View className="flex-row gap-2">
            <Text className="text-sm font-semibold text-primary">{t('members.detail.rewardAdjustment.reason')}:</Text>
            <Text className="text-sm font-normal text-primary">{reasonLabel}</Text>
          </View>
          {s.note ? (
            <View className="flex-row gap-2">
              <Text className="text-sm font-semibold text-primary">{t('members.detail.rewardAdjustment.note')}:</Text>
              <Text className="min-w-0 flex-1 text-sm font-normal text-secondary">{s.note}</Text>
            </View>
          ) : null}
        </View>
      </View>

      <View className="mt-6">
        <ModalActions>
          <Button
            label={t('common.cancel')}
            variant="secondary"
            fullWidth={false}
            onPress={onCancel}
          />
          <Button
            label={confirmLabel}
            variant="primary"
            className="flex-1"
            onPress={onConfirm}
          />
        </ModalActions>
      </View>
    </AdminModal>
  )
}